<template>
    <div ref="importDetailInformationTab" class="flex-column import-detail-information-tab">
        <div class="information">
            <h3>{{ $l10n.__('Informations') }}</h3>
            <ImportTag
                ref="importTag"
                :label="labelTagImport"
                :status="importDetail.status"
                size="medium"
            />
            <p>
                <span class="typographies-paragraph-medium-semibold">
                    {{ $l10n.__('Date :') }}
                </span>
                <span class="typographies-paragraph-medium-regular">
                    {{ $filters.saasDateFormatted(importDetail.created_at) }}
                </span>
            </p>
            <p>
                <span class="typographies-paragraph-medium-semibold">
                    {{ $l10n.__('Utilisateur :') }}
                </span>
                <span class="typographies-paragraph-medium-regular">
                    {{ getFullNameStaff(importDetail) }}
                </span>
            </p>
            <p>
                <span class="typographies-paragraph-medium-semibold">
                    {{ $l10n.__('Type d’import :') }}
                </span>
                <span class="typographies-paragraph-medium-regular">
                    {{ labelTypeImport }}
                </span>
            </p>
            <p>
                <span class="typographies-paragraph-medium-semibold">
                    {{ $l10n.__('Traitement :') }}
                </span>
                <span ref="labelDurationImport" class="typographies-paragraph-medium-regular">
                    {{ labelDurationImport }}
                </span>
            </p>
            <p>
                <span class="typographies-paragraph-medium-semibold">
                    {{ $l10n.__('Nombre total de lignes :') }}
                </span>
                <span class="typographies-paragraph-medium-regular">
                    {{ getTotalRows }}
                </span>
            </p>
        </div>
        <div class="actions">
            <h3>{{ $l10n.__('Actions') }}</h3>
            <div v-if="isImportCancellable(importDetail)" class="line-action">
                <p class="typographies-paragraph-medium-regular">
                    {{ $t.__('settings.import.detail.ImportDetailInformationTab.cancel_import') }}
                </p>
                <CompassButton
                    :label="
                        $t.__('settings.import.detail.ImportDetailInformationTab.cancel_import')
                    "
                    type="tertiary-destructive"
                    @click="onCancel"
                />
            </div>
            <div v-else-if="hasRevertAction" class="line-action">
                <p class="typographies-paragraph-medium-regular">
                    {{
                        $t.__(
                            'settings.import.detail.ImportDetailInformationTab.cancel_import_and_restore_data_label',
                        )
                    }}
                    <span v-if="remainingHoursToRollback" class="gray-60">
                        {{ remainingHoursToRollback }}
                    </span>
                </p>
                <CompassTooltip
                    contentClass="tooltip-disable-btn"
                    :disabled="isImportCanBeReverted(importDetail)"
                    :content="getRevertImportBtnLabel(importDetail)"
                >
                    <CompassButton
                        :disabled="!isImportCanBeReverted(importDetail)"
                        :label="
                            $t.__(
                                'settings.import.detail.ImportDetailInformationTab.cancel_import_and_restore_data',
                            )
                        "
                        type="tertiary-destructive"
                        @click="onRevert"
                    />
                </CompassTooltip>
            </div>
            <div class="line-action">
                <p class="typographies-paragraph-medium-regular">
                    {{ $l10n.__('Télécharger le fichier que vous avez utilisé pour votre import') }}
                </p>
                <CompassTooltip
                    :content="
                        $t.__(
                            'settings.import.detail.ImportDetailInformationTab.download_import_file_disabled_tooltip',
                        )
                    "
                    :disabled="!isDownloadFileDisabled"
                >
                    <CompassButton
                        :label="
                            $t.__(
                                'settings.import.detail.ImportDetailInformationTab.download_import_file',
                            )
                        "
                        type="tertiary"
                        :loading="isFileDownloading"
                        :disabled="isDownloadFileDisabled"
                        @click="onDownloadImportFile"
                    />
                </CompassTooltip>
            </div>
            <div v-if="isDownloadErrorFileButtonDisplayed" class="line-action">
                <p class="typographies-paragraph-medium-regular">
                    {{
                        $l10n.__(
                            'Télécharger le fichier des erreurs identifiées lors de votre import',
                        )
                    }}
                </p>
                <CompassTooltip
                    contentClass="tooltip-disable-btn"
                    :disabled="canDownloadErrorFile"
                    :content="
                        $t.__(
                            'settings.import.detail.ImportDetailInformationTab.action_available_when_import_processing_completed',
                        )
                    "
                >
                    <CompassButton
                        :label="
                            $t.__(
                                'settings.import.detail.ImportDetailInformationTab.download_error_file',
                            )
                        "
                        type="tertiary"
                        :disabled="!canDownloadErrorFile"
                        @click="onDownloadErrorFile"
                    />
                </CompassTooltip>
            </div>
        </div>
    </div>
</template>

<script>
import { importProviders, importStatus } from 'stores/settings/settingsImportsStore.js';
import { createNamespacedHelpers } from 'vuex';
import ImportTag from 'vcomponents/settings/import/ImportTag.vue';
import ImportStatusDictionary from 'Dictionaries/imports/ImportStatusDictionary';
const importsStore = createNamespacedHelpers('importsStore');
const popupPanelMapActions = createNamespacedHelpers('popupPanel').mapActions;
import importsMixins from 'vcomponents/settings/import/importsMixins.js';
export default {
    components: { ImportTag },
    mixins: [importsMixins],
    props: {
        isImportV2: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            isFileDownloading: false,
        };
    },
    computed: {
        ...importsStore.mapGetters({
            importDetail: 'getDetailImport',
        }),
        labelTypeImport() {
            return this.getMetadataImport.find((item) => item.type === this.importDetail.type)
                .title;
        },
        labelDurationImport() {
            if (this.isImportInErrorOrCancelled) {
                return '—';
            }
            if (importStatus.QUEUED === this.importDetail.status) {
                return this.$l10n.__('En attente');
            }
            if (this.importDetail.started_at) {
                const startDate = moment(this.importDetail.started_at).format(
                    `[${this.$l10n.__('Début le')}] DD MMMM YYYY [${this.$l10n.__('à')}] HH[h]mm`,
                );
                const endDate = this.importDetail.completed_at
                    ? moment(this.importDetail.completed_at).format(
                        `[${this.$l10n.__('Fin le')}] DD MMMM YYYY [${this.$l10n.__(
                            'à',
                        )}] HH[h]mm`,
                    )
                    : this.$l10n.__('En cours');
                return `${startDate} - ${endDate}`;
            } else {
                return '—';
            }
        },
        labelTagImport() {
            let statusLabel = ImportStatusDictionary[this.importDetail.status].status;
            if (
                [importStatus.REVERTING, importStatus.PROCESSING].includes(
                    this.importDetail.status,
                ) &&
                this.importDetail.total_rows > 0
            ) {
                const percentProgress = Math.round(
                    (this.importDetail.browsed_rows / this.importDetail.total_rows) * 100,
                );
                statusLabel = `${statusLabel} (${percentProgress} %)`;
            }
            return statusLabel;
        },
        remainingHoursToRollback() {
            if ([importStatus.PROCESSED, importStatus.FAILED].includes(this.importDetail.status)) {
                const end = moment(this.importDetail.completed_at).add(24, 'h');
                const now = new moment();
                const remainingTime = end.diff(now, 'h');
                if (0 >= remainingTime) {
                    const minutes = end.diff(now, 'm');
                    return 0 >= minutes
                        ? null
                        : `(${minutes}min ${this.$l10n.n__(
                            'restante',
                            'restantes',
                            minutes,
                            minutes,
                        )})`;
                }
                return `(${remainingTime}h ${this.$l10n.n__(
                    'restante',
                    'restantes',
                    remainingTime,
                    remainingTime,
                )})`;
            }
            return null;
        },
        urlToDownloadFile() {
            return `/imports/${this.importDetail.batch_id}/download`;
        },
        canDownloadErrorFile() {
            return [importStatus.PROCESSED, importStatus.REVERTED].includes(
                this.importDetail.status,
            );
        },
        isDownloadErrorFileButtonDisplayed() {
            return !this.isImportInErrorOrCancelled && this.importDetail.invalid_rows > 0;
        },
        isImportInErrorOrCancelled() {
            return [importStatus.CANCELLED, importStatus.FAILED].includes(this.importDetail.status);
        },
        getTotalRows() {
            return this.isImportInErrorOrCancelled ? '—' : this.importDetail.total_rows.toFixed(0);
        },
        hasRevertAction() {
            return (
                [importStatus.QUEUED, importStatus.PROCESSING, importStatus.PROCESSED].includes(
                    this.importDetail.status,
                ) &&
                this.importDetail.processed_rows > 0 &&
                [importProviders.FLATFILE_PLATFORM].includes(this.importDetail.provider)
            );
        },
        isDownloadFileDisabled() {
            const endDate = moment(this.importDetail.created_at).add(1, 'w');
            return moment().isAfter(endDate);
        },
    },
    methods: {
        ...popupPanelMapActions(['open']),
        onDownloadImportFile() {
            if (this.isImportV2) {
                this.downloadImportV2File();
                return;
            }
            this.isFileDownloading = true;
            fetch(this.urlToDownloadFile, {
                method: 'HEAD',
            })
                .then((res) => {
                    if (res.status === 204) {
                        const link = document.createElement('a');
                        link.href = this.urlToDownloadFile;
                        document.body.appendChild(link);
                        link.click();
                        link.parentNode.removeChild(link);
                    } else {
                        this.$pushToast({
                            type: 'error',
                            text: this.$l10n.__(
                                "Impossible de récupérer le fichier d'import, merci de réessayer",
                            ),
                            close: true,
                            icon: true,
                            unique: true,
                        });
                    }
                })
                .finally(() => (this.isFileDownloading = false));
        },
        onDownloadErrorFile() {
            //we have to specify errorFile for importV2 cause legacy import use same url as new import for download file
            this.isImportV2
                ? this.downloadImportV2File({ errorFile: true })
                : this.downloadImportV2File();
        },
        downloadImportV2File(errorFile = false) {
            let url = '/?_f=import&action=downloadFile&importID=' + this.importDetail.id;
            if (errorFile) {
                url += '&errors=Y';
            }
            const link = document.createElement('a');
            link.href = url;
            link.click();
        },
        onCancel() {
            this.open({
                key: 'importCancelPopup',
                props: {
                    batchId: this.importDetail.batch_id,
                    type: this.importDetail.type,
                },
            });
        },
        onRevert() {
            this.open({
                key: 'importRevertPopup',
                props: {
                    importToRevert: this.importDetail,
                },
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.import-detail-information-tab {
    font-size: var(--font-size-14);
    line-height: var(--line-heights-20);

    h3 {
        line-height: var(--line-heights-24);
    }

    .actions {
        margin-top: var(--spacing-4);
        h3 {
            border-bottom: 1px solid var(--colors-common-border-lighter);
            margin: 0;
            padding-bottom: var(--spacing-1);
        }
        .line-action {
            align-items: center;
            border-bottom: 1px solid var(--colors-common-border-lighter);
            display: flex;
            justify-content: space-between;
            padding: var(--spacing-1) 0;

            p {
                display: flex;
                flex-direction: row;
                gap: var(--spacing-0);
                margin: 0;
            }
        }
    }

    .information {
        align-items: flex-start;
        display: flex;
        flex-direction: column;
        gap: var(--spacing-0);

        h3 {
            margin-bottom: var(--spacing-0);
            margin-top: 0;
        }

        &:deep(.compass-tag) {
            margin: 0 0 var(--spacing-0);
        }
    }
}
</style>
